import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { UserPlus, Search, Mail, MoreVertical, Trash2, Send, ChevronDown, ChevronLeft, ChevronRight, Filter, Users, X, Upload, Plus, Globe, Building2, Check, User, ArrowUpDown, Phone, MinusCircle, PlusCircle, Link2 } from 'lucide-react';

import { MobileSubMenuHeader } from '@/components/layout';
import { navigationItems } from '@/lib/navigation';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { projectService } from '@/lib/projectService';
import { useWorkspace } from '@/context/WorkspaceContext';
import { toast } from 'sonner';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { uploadToR2 } from '@/lib/r2-storage';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { motion, AnimatePresence } from 'framer-motion';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useSearchParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { CompanyDetail } from './CompanyDetail';
import { ClientDetail } from './ClientDetail';
import { PhoneInputField } from '@/components/ui/phone-input';


interface ContactsProps {
  isGlobalDarkMode: boolean;
  onMobileSubMenuToggle?: (menuId: string | null, subItems?: any[], menuLabel?: string) => void;
  isMobile?: boolean;
}

interface ClientData {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: string;
  status: string;
  joinedAt: string;
  projectCount: number;
  isInvitation?: boolean;
  companyName?: string;
  country?: string;
  lastLogin?: string | null;
}

const Contacts: React.FC<ContactsProps> = ({ isGlobalDarkMode, onMobileSubMenuToggle, isMobile = false }) => {
  const [mobileSearchQuery, setMobileSearchQuery] = React.useState('');
  const { currentWorkspace } = useWorkspace();
  const [clients, setClients] = useState<ClientData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [inviting, setInviting] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [sendingLink, setSendingLink] = useState<string | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const companyFileInputRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'clients';

  const [companies, setCompanies] = useState<any[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [addCompanyOpen, setAddCompanyOpen] = useState(false);
  const [addingCompany, setAddingCompany] = useState(false);
  const [deleteCompanyId, setDeleteCompanyId] = useState<string | null>(null);
  const [deletingCompany, setDeletingCompany] = useState(false);

  // New Client Fields
  const [website, setWebsite] = useState('');
  const [companyName, setCompanyName] = useState('');

  const [designation, setDesignation] = useState('');
  const [emailType, setEmailType] = useState<'work' | 'personal' | 'other'>('work');
  const [phoneType, setPhoneType] = useState<'work' | 'personal' | 'other'>('work');
  const [socialLinks, setSocialLinks] = useState<{platform: string, url: string}[]>([{platform: 'linkedin', url: ''}]);
  const [additionalEmails, setAdditionalEmails] = useState<{email: string, type: 'work' | 'personal' | 'other'}[]>([]);
  const [additionalPhones, setAdditionalPhones] = useState<{phone: string, type: 'work' | 'personal' | 'other'}[]>([]);
  const [source, setSource] = useState('');
  
  // Company details
  const [companyMode, setCompanyMode] = useState<'existing' | 'new'>('existing');
  const [companyCategory, setCompanyCategory] = useState('');
  
  // Address fields
  const [country, setCountry] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [zip, setZip] = useState('');
  const [streetAddress, setStreetAddress] = useState('');

  const [avatarUrl, setAvatarUrl] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const [selectedCompanyClientIds, setSelectedCompanyClientIds] = useState<string[]>([]);
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [clientsCurrentPage, setClientsCurrentPage] = useState(1);
  const [clientsItemsPerPage, setClientsItemsPerPage] = useState(20);
  
  const [selectedCompanies, setSelectedCompanies] = useState<string[]>([]);
  const [companiesCurrentPage, setCompaniesCurrentPage] = useState(1);
  const [companiesItemsPerPage, setCompaniesItemsPerPage] = useState(20);

  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus();
    }
  }, [searchOpen]);

  const handleMobileMenuToggle = () => {
    const crmMenuItem = navigationItems.find(item => item.id === 'crm');
    if (crmMenuItem && onMobileSubMenuToggle) {
      onMobileSubMenuToggle('crm', crmMenuItem.subItems, crmMenuItem.label);
    }
  };

  const loadClients = useCallback(async () => {
    if (!currentWorkspace) return;
    try {
      setLoading(true);
      const data = await projectService.getWorkspaceClients(currentWorkspace.id);
      setClients(data);
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id]);

  const loadCompanies = useCallback(async () => {
    if (!currentWorkspace) return;
    try {
      setLoadingCompanies(true);
      const data = await projectService.getWorkspaceCompanies(currentWorkspace.id);
      setCompanies(data);
    } catch (error) {
      console.error('Error loading companies:', error);
    } finally {
      setLoadingCompanies(false);
    }
  }, [currentWorkspace?.id]);

  useEffect(() => {
    loadClients();
    loadCompanies();
  }, [loadClients, loadCompanies]);



  const handleAvatarUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }

    setUploadingAvatar(true);
    try {
      const fileName = `${Date.now()}-${file.name}`;
      const { publicUrl } = await uploadToR2({
        fileName,
        file,
        folder: 'client-avatars'
      });
      setAvatarUrl(publicUrl);
      toast.success('Profile image uploaded');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploadingAvatar(false);
    }
  };


  const handleInviteClient = async () => {
    if (!currentWorkspace || !inviteEmail.trim()) return;
    try {
      setInviting(true);
      
      const socialMediaObj: Record<string, string> = {};
      socialLinks.forEach(link => {
        if (link.url.trim() && link.platform) {
          socialMediaObj[link.platform] = link.url.trim();
        }
      });

      await projectService.inviteClient(
        currentWorkspace.id,
        inviteEmail.trim(),
        {
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          phone: phone.trim(),
          website: website.trim(),
          companyName: companyMode === 'new' ? companyName.trim() : (companies.find(c => c.id === selectedCompanyId.current)?.name || ''),
          companyId: companyMode === 'existing' && selectedCompanyId.current !== 'none' ? selectedCompanyId.current : undefined,
          category: companyMode === 'new' ? companyCategory.trim() : undefined,
          country: companyMode === 'new' ? country.trim() : undefined,
          state: companyMode === 'new' ? state.trim() : undefined,
          city: companyMode === 'new' ? city.trim() : undefined,
          zip: companyMode === 'new' ? zip.trim() : undefined,
          streetAddress: companyMode === 'new' ? streetAddress.trim() : undefined,
          sendEmail,
          designation: designation.trim() || undefined,
          emailType,
          phoneType,
          socialMedia: Object.keys(socialMediaObj).length > 0 ? socialMediaObj : undefined,
          additionalEmails: additionalEmails.length > 0 ? additionalEmails : undefined,
          additionalPhones: additionalPhones.length > 0 ? additionalPhones : undefined,
          source: source.trim() || undefined
        }
      );
      toast.success(sendEmail ? `Magic link sent to ${inviteEmail}` : `Client added to workspace`);

      // Reset states
      setInviteEmail('');
      setFirstName('');
      setLastName('');
      setPhone('');
      setWebsite('');
      setCompanyName('');

      setDesignation('');
      setEmailType('work');
      setPhoneType('work');
      setSocialLinks([{platform: 'linkedin', url: ''}]);
      setAdditionalEmails([]);
      setAdditionalPhones([]);
      setSource('');
      setCountry('');
      setState('');
      setCity('');
      setZip('');
      setStreetAddress('');
      setCompanyCategory('');
      setCompanyMode('existing');
      setAvatarUrl('');
      setSendEmail(true);
      setInviteOpen(false);
      loadClients();
    } catch (error: any) {
      toast.error(error.message || 'Failed to add client');
    } finally {
      setInviting(false);
    }
  };


  const handleSendMagicLink = async (client: ClientData) => {
    if (!currentWorkspace) return;
    try {
      setSendingLink(client.id);
      await projectService.sendMagicLink(currentWorkspace.id, client.email, client.name.split(' ')[0], client.name.split(' ')[1]);
      toast.success(`Magic link sent to ${client.email}`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to send magic link');
    } finally {
      setSendingLink(null);
    }
  };

  const handleDeleteClient = async () => {
    if (!currentWorkspace || !deleteId) return;
    try {
      setDeleting(true);
      await projectService.deleteClient(currentWorkspace.id, deleteId);
      toast.success('Client removed from workspace');
      setDeleteId(null);
      loadClients();
    } catch (error: any) {
      toast.error(error.message || 'Failed to remove client');
    } finally {
      setDeleting(false);
    }
  };

  const filteredClients = clients.filter(c => {
    const matchesSearch = !searchQuery ||
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesStatus = statusFilter === 'all' ||
      (statusFilter === 'active' && !c.isInvitation) ||
      (statusFilter === 'pending' && c.isInvitation);

    return matchesSearch && matchesStatus;
  });

  const filteredCompanies = companies.filter(c => {
    return !searchQuery ||
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (c.email && c.email.toLowerCase().includes(searchQuery.toLowerCase()));
  });

  const clientsTotalPages = Math.ceil(filteredClients.length / clientsItemsPerPage);
  const paginatedClients = filteredClients.slice((clientsCurrentPage - 1) * clientsItemsPerPage, clientsCurrentPage * clientsItemsPerPage);

  const companiesTotalPages = Math.ceil(filteredCompanies.length / companiesItemsPerPage);
  const paginatedCompanies = filteredCompanies.slice((companiesCurrentPage - 1) * companiesItemsPerPage, companiesCurrentPage * companiesItemsPerPage);

  const handleAddCompany = async () => {
    if (!currentWorkspace || !companyName.trim()) return;
    try {
      setAddingCompany(true);
      await projectService.createCompany(currentWorkspace.id, {
        name: companyName.trim(),
        website: website.trim(),
        email: inviteEmail.trim(),
        phone: phone.trim(),

        country: country.trim(),
        state: state.trim(),
        city: city.trim(),
        zip: zip.trim(),
        street_address: streetAddress.trim(),
        logo_url: avatarUrl,
        clientIds: selectedCompanyClientIds
      });
      toast.success('Company created successfully');
      setAddCompanyOpen(false);

      // Reset states
      setCompanyName('');
      setAvatarUrl('');
      setWebsite('');
      setInviteEmail('');
      setPhone('');

      setCountry('');
      setState('');
      setCity('');
      setZip('');
      setStreetAddress('');

      loadCompanies();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create company');
    } finally {
      setAddingCompany(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!currentWorkspace || !deleteCompanyId) return;
    try {
      setDeletingCompany(true);
      await projectService.deleteCompany(deleteCompanyId);
      toast.success('Company removed');
      setDeleteCompanyId(null);
      loadCompanies();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete company');
    } finally {
      setDeletingCompany(false);
    }
  };

  const selectedCompanyId = React.useRef<string | undefined>(undefined);

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Clients | AgencyPlus</title>
        <meta name="description" content="Manage your clients and customer relationships" />
        <link rel="canonical" href="/crm/clients" />
      </Helmet>

      {isMobile && (
        <MobileSubMenuHeader
          onMenuClick={handleMobileMenuToggle}
          onAddClick={() => setInviteOpen(true)}
          searchValue={mobileSearchQuery}
          onSearchChange={setMobileSearchQuery}
          searchPlaceholder="Search clients..."
          showSearch={true}
          showAdd={true}
          isDarkMode={isGlobalDarkMode}
        />
      )}

      <div className={`w-full px-6 md:px-10 py-6 ${isMobile ? 'pt-20' : ''}`}>
        {/* Unified Header */}
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Clients</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your clients and send magic link invitations
            </p>
          </div>

          <div className="flex items-center gap-2">
            {/* Search — collapsible */}
            <div className="relative flex items-center h-10">
              <AnimatePresence initial={false}>
                {searchOpen ? (
                  <motion.div
                    key="search-open"
                    className="relative flex items-center h-10"
                    initial={{ width: 40, opacity: 0.6 }}
                    animate={{ width: 256, opacity: 1 }}
                    exit={{ width: 40, opacity: 0 }}
                    transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                  >
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="w-4 h-4 text-muted-foreground" />
                    </div>
                    <input
                      ref={searchInputRef}
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search clients..."
                      onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                      className="w-full h-full pl-9 pr-8 text-sm rounded-lg border transition-colors bg-card border-border  focus:ring-1 focus:ring-workspace-accent/30"
                    />
                    <button
                      onClick={() => { setSearchQuery(''); setSearchOpen(false); }}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-muted-foreground hover:text-foreground"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </motion.div>
                ) : (
                  <motion.button
                    key="search-icon"
                    onClick={() => setSearchOpen(true)}
                    className="flex items-center justify-center w-10 h-10 rounded-lg border bg-card border-border  hover:bg-muted transition-colors"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <Search className="w-4 h-4" />
                  </motion.button>
                )}
              </AnimatePresence>
            </div>

            {/* Filter Toggle */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className={`flex items-center gap-2 px-4 h-10 rounded-lg border transition-colors whitespace-nowrap ${statusFilter !== 'all'
                    ? 'bg-workspace-accent text-white border-transparent'
                    : 'bg-card border-border  hover:bg-muted'
                  }`}>
                  <Filter className="w-4 h-4" />
                  <span className="text-sm font-medium">{statusFilter === 'all' ? 'Filter' : statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1)}</span>
                  <ChevronDown className="w-3.5 h-3.5 opacity-50" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-popover border-border">
                <DropdownMenuItem onClick={() => setStatusFilter('all')} className="flex items-center justify-between">
                  All Clients {statusFilter === 'all' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('active')} className="flex items-center justify-between">
                  Active {statusFilter === 'active' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setStatusFilter('pending')} className="flex items-center justify-between">
                  Pending {statusFilter === 'pending' && <div className="w-2 h-2 bg-workspace-accent rounded-full" />}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Add Button */}
            <button
              onClick={() => activeTab === 'clients' ? setInviteOpen(true) : setAddCompanyOpen(true)}
              className="flex items-center gap-2 px-6 h-10 bg-workspace-accent text-white rounded-lg hover:bg-workspace-accent-hover transition-colors whitespace-nowrap"
            >
              <Plus className="w-4 h-4" />
              <span className="font-medium">Add {activeTab === 'clients' ? 'Client' : 'Company'}</span>
            </button>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={(val) => setSearchParams({ tab: val })} className="w-full">
          <TabsList className="mb-6 bg-transparent border-b border-border w-full justify-start rounded-none p-0 h-auto space-x-6">
            <TabsTrigger
              value="clients"
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-workspace-accent data-[state=active]:text-foreground text-muted-foreground rounded-none px-0 pb-3 pt-2 text-sm font-medium border-b-2 border-transparent"
            >
              Individual Clients
            </TabsTrigger>
            <TabsTrigger
              value="companies"
              className="data-[state=active]:bg-transparent data-[state=active]:border-b-2 data-[state=active]:border-workspace-accent data-[state=active]:text-foreground text-muted-foreground rounded-none px-0 pb-3 pt-2 text-sm font-medium border-b-2 border-transparent"
            >
              Companies
            </TabsTrigger>
          </TabsList>

          <TabsContent value="clients" className="mt-0">
            {/* Summary Cards */}
            {!searchParams.get('clientId') && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="p-6 rounded-2xl bg-card border border-border shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Total Clients</span>
                    <Users className="w-5 h-5 text-workspace-accent opacity-20" />
                  </div>
                  <div className="text-3xl font-bold text-foreground">{clients.length}</div>
                </div>
                <div className="p-6 rounded-2xl bg-card border border-border shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Active Members</span>
                    <div className="w-2 h-2 bg-emerald-500 rounded-full" />
                  </div>
                  <div className="text-3xl font-bold text-foreground">{clients.filter(c => c.status === 'active').length}</div>
                </div>
                <div className="p-6 rounded-2xl bg-card border border-border shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-muted-foreground uppercase tracking-wider">Assignments</span>
                    <Send className="w-5 h-5 text-blue-500 opacity-20" />
                  </div>
                  <div className="text-3xl font-bold text-foreground">{clients.reduce((sum, c) => sum + c.projectCount, 0)}</div>
                </div>
              </div>
            )}

            {/* Clients Table */}
            {searchParams.get('clientId') ? (
              <ClientDetail
                id={searchParams.get('clientId')!}
                onBack={() => setSearchParams({ tab: 'clients' })}
              />
            ) : loading ? (
              null
            ) : filteredClients.length > 0 ? (
              <div className="rounded-xl border border-border bg-white dark:bg-card overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="px-6 py-3.5 text-xs font-semibold text-muted-foreground w-12 text-center">
                        <Checkbox 
                          className="rounded flex mx-auto border-muted-foreground/30 data-[state=checked]:bg-workspace-accent data-[state=checked]:border-workspace-accent"
                          checked={filteredClients.length > 0 && selectedClients.length === filteredClients.length}
                          onCheckedChange={(checked) => {
                            if (checked) setSelectedClients(filteredClients.map(c => c.id));
                            else setSelectedClients([]);
                          }}
                        />
                      </th>
                      <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Name</th>
                      <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Email</th>
                      <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">Company</th>
                      <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden lg:table-cell">Location</th>
                      <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden xl:table-cell">Joined</th>
                      <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider hidden md:table-cell">Last Login</th>
                      <th className="text-right px-6 py-3.5 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedClients.map((client) => (
                      <tr 
                        key={client.id} 
                        className={`border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer`}
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest('.no-row-click')) return;
                          setSearchParams({ tab: 'clients', clientId: client.id });
                        }}
                      >
                        <td className="px-6 py-4 align-top pt-5 no-row-click">
                          <Checkbox 
                            className="rounded mx-auto block border-muted-foreground/30 data-[state=checked]:bg-workspace-accent data-[state=checked]:border-workspace-accent"
                            checked={selectedClients.includes(client.id)}
                            onCheckedChange={(checked) => {
                              if (checked) setSelectedClients([...selectedClients, client.id]);
                              else setSelectedClients(selectedClients.filter(id => id !== client.id));
                            }}
                          />
                        </td>
                        <td className="px-6 py-4 align-top">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-workspace-accent/15 flex items-center justify-center text-sm font-semibold text-workspace-accent overflow-hidden flex-shrink-0">
                              {client.avatar ? (
                                <img src={client.avatar} alt="" className="w-full h-full object-cover" />
                              ) : (
                                (client.name[0] || 'C').toUpperCase()
                              )}
                            </div>
                            <div>
                              <div className="font-medium text-foreground transition-colors">{client.name}</div>
                              <div className="text-xs text-muted-foreground md:hidden">{client.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-muted-foreground hidden lg:table-cell align-top">{client.email}</td>
                        <td className="px-6 py-4 text-sm text-muted-foreground hidden md:table-cell align-top">
                          {client.companyName || <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className="px-6 py-4 text-sm text-muted-foreground hidden lg:table-cell align-top">
                          {client.country || <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className="px-6 py-4 text-sm text-muted-foreground hidden xl:table-cell align-top">
                          {new Date(client.joinedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </td>
                        <td className="px-6 py-4 text-sm text-muted-foreground hidden md:table-cell align-top">
                          {client.lastLogin ? (
                            new Date(client.lastLogin).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })
                          ) : client.isInvitation ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold bg-yellow-500/10 text-yellow-500 uppercase tracking-wide">
                              Pending
                            </span>
                          ) : (
                            <span className="text-muted-foreground/40">Never</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right align-top pt-5 no-row-click">
                          <div className="flex items-center justify-end gap-2">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button className="p-2 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
                                  <MoreVertical className="w-4 h-4" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem
                                  onClick={() => setSearchParams({ tab: 'clients', clientId: client.id })}
                                >
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleSendMagicLink(client)}
                                  disabled={!!sendingLink}
                                >
                                  <Send className="w-4 h-4" />
                                  {sendingLink === client.id ? 'Sending...' : 'Send Magic Link'}
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  destructive
                                  onClick={() => setDeleteId(client.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                  Remove Client
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Pagination Controls */}
                {filteredClients.length > 0 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-transparent">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <span>Show</span>
                        <Select value={clientsItemsPerPage.toString()} onValueChange={(val) => { setClientsItemsPerPage(Number(val)); setClientsCurrentPage(1); }}>
                          <SelectTrigger className="h-8 w-[70px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                            <SelectItem value="200">200</SelectItem>
                            <SelectItem value="500">500</SelectItem>
                          </SelectContent>
                        </Select>
                        <span>per page</span>
                      </div>
                      <span>Showing {(clientsCurrentPage - 1) * clientsItemsPerPage + 1} to {Math.min(clientsCurrentPage * clientsItemsPerPage, filteredClients.length)} of {filteredClients.length} clients</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setClientsCurrentPage(p => Math.max(1, p - 1))} disabled={clientsCurrentPage === 1} className="h-8 w-8 p-0">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <div className="text-sm font-medium mx-2">Page {clientsCurrentPage} of {clientsTotalPages || 1}</div>
                      <Button variant="outline" size="sm" onClick={() => setClientsCurrentPage(p => Math.min(clientsTotalPages, p + 1))} disabled={clientsCurrentPage >= clientsTotalPages || clientsTotalPages === 0} className="h-8 w-8 p-0">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 bg-workspace-accent/10">
                  <UserPlus className="w-10 h-10 text-workspace-accent" />
                </div>
                <h2 className="text-xl font-bold mb-2 text-foreground">
                  {searchQuery ? 'No clients found' : 'No clients yet'}
                </h2>
                <p className="text-center max-w-md mb-6 text-muted-foreground">
                  {searchQuery
                    ? 'Try adjusting your search'
                    : 'Invite your first client via magic link. They\'ll receive a passwordless login email.'}
                </p>
                {!searchQuery && (
                  <Button
                    onClick={() => setInviteOpen(true)}
                    className="bg-workspace-accent text-white hover:bg-workspace-accent-hover"
                  >
                    <UserPlus className="mr-2 h-4 w-4" /> Add Client
                  </Button>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="companies" className="mt-0">
            {searchParams.get('companyId') ? (
              <CompanyDetail
                id={searchParams.get('companyId')!}
                onBack={() => setSearchParams({ tab: 'companies' })}
              />
            ) : loadingCompanies ? (
              null
            ) : filteredCompanies.length > 0 ? (
              <div className="rounded-xl border border-border bg-white dark:bg-card overflow-hidden">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="w-12 px-6 py-3.5 text-center">
                        <Checkbox 
                          className="rounded flex mx-auto border-muted-foreground/30 data-[state=checked]:bg-workspace-accent data-[state=checked]:border-workspace-accent"
                          checked={filteredCompanies.length > 0 && selectedCompanies.length === filteredCompanies.length}
                          onCheckedChange={(checked) => {
                            if (checked) setSelectedCompanies(filteredCompanies.map(c => c.id));
                            else setSelectedCompanies([]);
                          }}
                        />
                      </th>
                      <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground tracking-wider">
                        <div className="flex items-center gap-1.5">
                          Company Name
                        </div>
                      </th>
                      <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground tracking-wider">Phone</th>
                      <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground tracking-wider">Website</th>
                      <th className="text-left px-6 py-3.5 text-xs font-semibold text-muted-foreground tracking-wider">Company Owner</th>
                      <th className="text-right px-6 py-3.5 border-l border-border w-12">
                        <ArrowUpDown className="w-4 h-4 text-muted-foreground mx-auto cursor-pointer" />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedCompanies.map((company) => (
                      <tr 
                        key={company.id} 
                        className="border-b border-border last:border-0 hover:bg-muted/30 transition-colors cursor-pointer"
                        onClick={(e) => {
                          if ((e.target as HTMLElement).closest('.no-row-click')) return;
                          setSearchParams({ tab: 'companies', companyId: company.id });
                        }}
                      >
                        <td className="px-6 py-4 align-top pt-5 no-row-click">
                          <Checkbox 
                            className="rounded mx-auto block border-muted-foreground/30 data-[state=checked]:bg-workspace-accent data-[state=checked]:border-workspace-accent"
                            checked={selectedCompanies.includes(company.id)}
                            onCheckedChange={(checked) => {
                              if (checked) setSelectedCompanies([...selectedCompanies, company.id]);
                              else setSelectedCompanies(selectedCompanies.filter(id => id !== company.id));
                            }}
                          />
                        </td>
                        <td className="px-6 py-4 align-top">
                          <span className="text-sm font-medium text-foreground transition-colors">
                            {company.name}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-muted-foreground">
                          {company.phone ? (
                            <div className="flex items-center gap-2">
                              {company.phone}
                              <Phone className="w-3.5 h-3.5 text-muted-foreground/50" />
                            </div>
                          ) : (
                            <span className="text-muted-foreground/40">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-muted-foreground">
                          {company.website ? (
                            <a href={company.website.startsWith('http') ? company.website : `http://${company.website}`} target="_blank" rel="noopener noreferrer" className="hover:underline">
                              {company.website.startsWith('http') ? company.website : `http://${company.website}`}
                            </a>
                          ) : (
                            <span className="text-muted-foreground/40">—</span>
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-muted-foreground">
                          Shanto Asif
                        </td>
                        <td className="px-6 py-4 text-right border-l border-border align-top pt-5 no-row-click">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-all">
                                <MoreVertical className="w-4 h-4 mx-auto" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => setSearchParams({ tab: 'companies', companyId: company.id })}>
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem destructive onClick={() => setDeleteCompanyId(company.id)}>
                                Delete Company
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Pagination Controls */}
                {filteredCompanies.length > 0 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-border bg-transparent">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <div className="flex items-center gap-2">
                        <span>Show</span>
                        <Select value={companiesItemsPerPage.toString()} onValueChange={(val) => { setCompaniesItemsPerPage(Number(val)); setCompaniesCurrentPage(1); }}>
                          <SelectTrigger className="h-8 w-[70px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="20">20</SelectItem>
                            <SelectItem value="50">50</SelectItem>
                            <SelectItem value="100">100</SelectItem>
                            <SelectItem value="200">200</SelectItem>
                            <SelectItem value="500">500</SelectItem>
                          </SelectContent>
                        </Select>
                        <span>per page</span>
                      </div>
                      <span>Showing {(companiesCurrentPage - 1) * companiesItemsPerPage + 1} to {Math.min(companiesCurrentPage * companiesItemsPerPage, filteredCompanies.length)} of {filteredCompanies.length} companies</span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm" onClick={() => setCompaniesCurrentPage(p => Math.max(1, p - 1))} disabled={companiesCurrentPage === 1} className="h-8 w-8 p-0">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <div className="text-sm font-medium mx-2">Page {companiesCurrentPage} of {companiesTotalPages || 1}</div>
                      <Button variant="outline" size="sm" onClick={() => setCompaniesCurrentPage(p => Math.min(companiesTotalPages, p + 1))} disabled={companiesCurrentPage >= companiesTotalPages || companiesTotalPages === 0} className="h-8 w-8 p-0">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20">
                <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6 bg-workspace-accent/10">
                  <Building2 className="w-10 h-10 text-workspace-accent" />
                </div>
                <h2 className="text-xl font-bold mb-2 text-foreground">
                  {searchQuery ? 'No companies found' : 'No companies yet'}
                </h2>
                <p className="text-center max-w-md mb-6 text-muted-foreground">
                  {searchQuery
                    ? 'Try adjusting your search'
                    : 'Group your clients by adding companies.'}
                </p>
                {!searchQuery && (
                  <Button
                    onClick={() => setAddCompanyOpen(true)}
                    className="bg-workspace-accent text-white hover:bg-workspace-accent-hover"
                  >
                    <Plus className="mr-2 h-4 w-4" /> Add Company
                  </Button>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Add Client Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent className="max-w-4xl bg-card border-border p-0 overflow-hidden">
          <DialogDescription className="sr-only">Add a client and send magic link</DialogDescription>
          <DialogHeader className="px-6 py-4 border-b border-border bg-muted/20">
            <DialogTitle className="text-xl font-bold text-foreground flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-workspace-accent" /> Add New Client
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="max-h-[80vh]">
            <div className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column - Main Details */}
                <div className="space-y-6">
                  {/* Profile Image & Basic Information Section */}
                  <Accordion type="multiple" defaultValue={["general"]} className="w-full">
                    <AccordionItem value="general" className="border-border">
                      <AccordionTrigger className="text-xs text-muted-foreground uppercase tracking-wider hover:no-underline py-4 pt-0">
                        General
                      </AccordionTrigger>
                      <AccordionContent className="space-y-6 pb-4">
                    {/* Top: Profile Image */}
                    <div className="space-y-2">
                      <Label className="text-foreground block text-center sm:text-left">Profile Image</Label>
                      <div className="flex justify-center sm:justify-start">
                        <div
                          onClick={() => fileInputRef.current?.click()}
                          className="relative group cursor-pointer w-36"
                        >
                          <div className={`
                            w-full aspect-square rounded-xl border-2 border-dashed 
                            flex flex-col items-center justify-center gap-1 transition-all p-3 text-center
                            ${avatarUrl ? 'border-workspace-accent bg-workspace-accent/5' : 'border-border hover:border-workspace-accent/50 hover:bg-muted/50'}
                          `}>
                            {uploadingAvatar ? (
                              <LoadingSpinner className="h-6 w-6" />
                            ) : avatarUrl ? (
                              <div className="flex flex-col items-center gap-1 w-full h-full justify-center">
                                <div className="w-16 h-16 rounded-full border-2 border-workspace-accent overflow-hidden shadow-lg">
                                  <img src={avatarUrl} alt="Preview" className="w-full h-full object-cover" />
                                </div>
                                <span className="text-[10px] font-medium text-workspace-accent">Click to change</span>
                              </div>
                            ) : (
                              <>
                                <div className="p-2 rounded-full bg-workspace-accent/10 text-workspace-accent group-hover:scale-110 transition-transform">
                                  <Upload className="w-5 h-5" />
                                </div>
                                <div className="text-center">
                                  <p className="text-xs font-semibold text-workspace-accent">Upload Photo</p>
                                  <p className="text-[10px] text-muted-foreground mt-0.5">PNG, JPG (5MB)</p>
                                </div>
                              </>
                            )}
                          </div>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleAvatarUpload}
                            accept="image/*"
                            className="hidden"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Basic Information */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-foreground flex items-center gap-1.5">First Name <span className="text-red-500">*</span></Label>
                        <Input
                          placeholder="John"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          className="bg-background border-input text-foreground h-11"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-foreground flex items-center gap-1.5">Last Name</Label>
                        <Input
                          placeholder="Doe"
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                          className="bg-background border-input text-foreground h-11"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-foreground flex items-center gap-1.5">Client Source</Label>
                      <Input value={source} onChange={e => setSource(e.target.value)} placeholder="e.g. Website, Referral" className="bg-background border-input text-foreground h-11" />
                    </div>
                      </AccordionContent>
                    </AccordionItem>
                  
                    <AccordionItem value="contact-info" className="border-border">
                      <AccordionTrigger className="text-xs text-muted-foreground uppercase tracking-wider hover:no-underline py-4">
                        Contact Information
                      </AccordionTrigger>
                      <AccordionContent className="space-y-6 pb-4">
                        <div className="space-y-4">
                          <div className="flex gap-2 items-center">
                            <Input className="flex-1 h-11 bg-background border-input text-foreground" type="email" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="Default Email *" required />
                            <div className="w-[100px]">
                              <Select value={emailType} onValueChange={(val: any) => setEmailType(val)}>
                                <SelectTrigger className="h-11 bg-background border-input"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-popover border-border">
                                  <SelectItem value="work">Work</SelectItem>
                                  <SelectItem value="personal">Personal</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          {additionalEmails.map((ae, idx) => (
                            <div key={`email-${idx}`} className="flex gap-2 items-center">
                              <Input className="flex-1 h-11 bg-background border-input text-foreground" type="email" value={ae.email} onChange={e => {
                                const newEmails = [...additionalEmails];
                                newEmails[idx].email = e.target.value;
                                setAdditionalEmails(newEmails);
                              }} placeholder="Additional Email" />
                              <div className="w-[100px]">
                                <Select value={ae.type} onValueChange={(val: any) => {
                                  const newEmails = [...additionalEmails];
                                  newEmails[idx].type = val;
                                  setAdditionalEmails(newEmails);
                                }}>
                                  <SelectTrigger className="h-11 bg-background border-input"><SelectValue /></SelectTrigger>
                                  <SelectContent className="bg-popover border-border">
                                    <SelectItem value="work">Work</SelectItem>
                                    <SelectItem value="personal">Personal</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <button type="button" onClick={() => setAdditionalEmails(additionalEmails.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-red-500 transition-colors">
                                <MinusCircle className="w-5 h-5" />
                              </button>
                            </div>
                          ))}
                          
                          <button type="button" onClick={() => setAdditionalEmails([...additionalEmails, {email: '', type: 'personal'}])} className="text-xs text-workspace-accent font-medium flex items-center gap-1 hover:underline">
                            <PlusCircle className="w-3 h-3" /> Add Email
                          </button>
                        </div>
                        
                        <div className="space-y-4 pt-2">
                          <div className="flex gap-2 items-center">
                            <PhoneInputField value={phone} onChange={setPhone} placeholder="Default Phone" />
                            <div className="w-[100px]">
                              <Select value={phoneType} onValueChange={(val: any) => setPhoneType(val)}>
                                <SelectTrigger className="h-11 bg-background border-input"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-popover border-border">
                                  <SelectItem value="work">Work</SelectItem>
                                  <SelectItem value="personal">Personal</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          
                          {additionalPhones.map((ap, idx) => (
                            <div key={`phone-${idx}`} className="flex gap-2 items-center">
                              <PhoneInputField value={ap.phone} onChange={e => {
                                const newPhones = [...additionalPhones];
                                newPhones[idx].phone = e;
                                setAdditionalPhones(newPhones);
                              }} placeholder="Additional Phone" />
                              <div className="w-[100px]">
                                <Select value={ap.type} onValueChange={(val: any) => {
                                  const newPhones = [...additionalPhones];
                                  newPhones[idx].type = val;
                                  setAdditionalPhones(newPhones);
                                }}>
                                  <SelectTrigger className="h-11 bg-background border-input"><SelectValue /></SelectTrigger>
                                  <SelectContent className="bg-popover border-border">
                                    <SelectItem value="work">Work</SelectItem>
                                    <SelectItem value="personal">Personal</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <button type="button" onClick={() => setAdditionalPhones(additionalPhones.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-red-500 transition-colors">
                                <MinusCircle className="w-5 h-5" />
                              </button>
                            </div>
                          ))}
                          
                          <button type="button" onClick={() => setAdditionalPhones([...additionalPhones, {phone: '', type: 'personal'}])} className="text-xs text-workspace-accent font-medium flex items-center gap-1 hover:underline">
                            <PlusCircle className="w-3 h-3" /> Add Phone
                          </button>
                        </div>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="social-profiles" className="border-border border-b-0">
                      <AccordionTrigger className="text-xs text-muted-foreground uppercase tracking-wider hover:no-underline py-4">
                        Social Profiles
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pb-4">
                        {socialLinks.map((sl, idx) => (
                          <div key={`social-${idx}`} className="flex gap-2 items-center">
                            <div className="w-[130px]">
                              <Select value={sl.platform} onValueChange={(val: any) => {
                                const newLinks = [...socialLinks];
                                newLinks[idx].platform = val;
                                setSocialLinks(newLinks);
                              }}>
                                <SelectTrigger className="h-11 bg-background border-input"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-popover border-border">
                                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                                  <SelectItem value="twitter">X / Twitter</SelectItem>
                                  <SelectItem value="facebook">Facebook</SelectItem>
                                  <SelectItem value="instagram">Instagram</SelectItem>
                                  <SelectItem value="youtube">YouTube</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="flex-1 relative">
                              <Link2 className="w-4 h-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                              <Input className="w-full h-11 bg-background border-input text-foreground pl-10" value={sl.url} onChange={e => {
                                const newLinks = [...socialLinks];
                                newLinks[idx].url = e.target.value;
                                setSocialLinks(newLinks);
                              }} placeholder="Profile URL" />
                            </div>
                            <button type="button" onClick={() => setSocialLinks(socialLinks.filter((_, i) => i !== idx))} className="text-muted-foreground hover:text-red-500 transition-colors">
                              <MinusCircle className="w-5 h-5" />
                            </button>
                          </div>
                        ))}
                        
                        <button type="button" onClick={() => setSocialLinks([...socialLinks, {platform: 'linkedin', url: ''}])} className="text-xs text-workspace-accent font-medium flex items-center gap-1 hover:underline">
                          <PlusCircle className="w-3 h-3" /> Add Social Link
                        </button>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>

                {/* Right Column - Company Details & Settings */}
                <div className="space-y-6 flex flex-col h-full">
                  <Accordion type="multiple" defaultValue={["company-details"]} className="w-full">
                    <AccordionItem value="company-details" className="border-border">
                      <AccordionTrigger className="text-xs text-muted-foreground uppercase tracking-wider hover:no-underline py-4 pt-0">
                        Company Details
                      </AccordionTrigger>
                      <AccordionContent className="space-y-6 pb-4">
                        <Tabs value={companyMode} onValueChange={(v: any) => setCompanyMode(v)} className="w-full">
                          <TabsList className="grid w-full grid-cols-2 mb-4 bg-muted/50">
                            <TabsTrigger value="existing">Link Company</TabsTrigger>
                            <TabsTrigger value="new">Edit Company</TabsTrigger>
                          </TabsList>
                          
                          <TabsContent value="existing" className="space-y-4 mt-0">
                            <div className="space-y-2">
                              <Label className="text-foreground flex items-center gap-1.5">Select Company</Label>
                              <Select onValueChange={(val) => { selectedCompanyId.current = val; setCompanyName(val === 'none' ? '' : companies.find(c => c.id === val)?.name || ''); }}>
                                <SelectTrigger className="bg-background border-input text-foreground h-11">
                                  <div className="flex items-center gap-2">
                                    <Building2 className="w-4 h-4 text-muted-foreground" />
                                    <SelectValue placeholder="Select a Company" />
                                  </div>
                                </SelectTrigger>
                                <SelectContent className="bg-popover border-border">
                                  <SelectItem value="none">No Company</SelectItem>
                                  {companies.map(c => (
                                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <p className="text-[11px] text-muted-foreground pl-1 italic">
                                Select an existing company to link this client.
                              </p>
                            </div>

                            <div className="space-y-2">
                              <Label className="text-foreground flex items-center gap-1.5">Designation</Label>
                              <Input value={designation} onChange={e => setDesignation(e.target.value)} className="bg-background border-input text-foreground h-11" placeholder="e.g. Certified Life Coach" />
                            </div>
                          </TabsContent>
                          
                          <TabsContent value="new" className="space-y-6 mt-0">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label className="text-foreground">Company Name</Label>
                                <Input placeholder="e.g. Acme Corp" value={companyName} onChange={e => setCompanyName(e.target.value)} className="bg-background border-input text-foreground h-11" />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-foreground">Category / Industry</Label>
                                <Input placeholder="e.g. Oil & Energy" value={companyCategory} onChange={e => setCompanyCategory(e.target.value)} className="bg-background border-input text-foreground h-11" />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label className="text-foreground flex items-center gap-1.5">Website</Label>
                              <div className="relative">
                                <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <Input
                                  placeholder="https://example.com"
                                  value={website}
                                  onChange={(e) => setWebsite(e.target.value)}
                                  className="bg-background border-input text-foreground h-11 pl-10"
                                />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label className="text-foreground flex items-center gap-1.5">Designation</Label>
                              <Input value={designation} onChange={e => setDesignation(e.target.value)} className="bg-background border-input text-foreground h-11" placeholder="e.g. Certified Life Coach" />
                            </div>
                          </TabsContent>
                        </Tabs>
                      </AccordionContent>
                    </AccordionItem>

                    <AccordionItem value="address" className="border-border border-b-0">
                      <AccordionTrigger className="text-xs text-muted-foreground uppercase tracking-wider hover:no-underline py-4">
                        Address
                      </AccordionTrigger>
                      <AccordionContent className="space-y-4 pb-4">
                        <div className="space-y-2">
                          <Label className="text-foreground">Street Address</Label>
                          <Input placeholder="123 Main St" value={streetAddress} onChange={e => setStreetAddress(e.target.value)} className="bg-background border-input text-foreground h-11" />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-foreground">City</Label>
                            <Input placeholder="New York" value={city} onChange={e => setCity(e.target.value)} className="bg-background border-input text-foreground h-11" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-foreground">State / Province</Label>
                            <Input placeholder="NY" value={state} onChange={e => setState(e.target.value)} className="bg-background border-input text-foreground h-11" />
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label className="text-foreground">ZIP / Postal Code</Label>
                            <Input placeholder="10001" value={zip} onChange={e => setZip(e.target.value)} className="bg-background border-input text-foreground h-11" />
                          </div>
                          <div className="space-y-2">
                            <Label className="text-foreground">Country</Label>
                            <Input placeholder="United States" value={country} onChange={e => setCountry(e.target.value)} className="bg-background border-input text-foreground h-11" />
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>

                  {/* Notification Control */}
                  <div className="flex items-center space-x-3 p-4 rounded-xl bg-workspace-accent/5 mt-auto">
                    <Checkbox
                      id="sendEmail"
                      checked={sendEmail}
                      onCheckedChange={(checked) => setSendEmail(!!checked)}
                      className="border-workspace-accent data-[state=checked]:bg-workspace-accent w-5 h-5"
                    />
                    <div className="space-y-0.5">
                      <label
                        htmlFor="sendEmail"
                        className="text-sm font-semibold text-foreground cursor-pointer"
                      >
                        Send magic link invitation now
                      </label>
                      <p className="text-xs text-muted-foreground">Client will receive a passwordless login link via email.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="px-6 py-4 border-t border-border bg-muted/20 gap-3">
            <Button variant="outline" onClick={() => setInviteOpen(false)} className="h-11 px-6">Cancel</Button>
            <Button
              onClick={handleInviteClient}
              disabled={inviting || !inviteEmail.trim() || !firstName.trim()}
              className="bg-workspace-accent text-white hover:bg-workspace-accent-hover h-11 px-8 shadow-md hover:shadow-lg transition-all"
            >
              {inviting ? (
                <div className="flex items-center gap-2">
                  <LoadingSpinner className="w-4 h-4 border-white/30 border-t-white" />
                  <span>Adding...</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Plus className="w-4 h-4" />
                  <span>{sendEmail ? 'Add & Send Link' : 'Add Client Only'}</span>
                </div>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" /> Remove Client
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to remove this client from the workspace?
              They will no longer have access to their projects and portals.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="secondary" onClick={() => setDeleteId(null)}>Cancel</Button>
            <Button
              onClick={handleDeleteClient}
              disabled={deleting}
              variant="destructive"
            >
              {deleting ? 'Removing...' : 'Remove Client'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Company Dialog */}
      <Dialog open={!!deleteCompanyId} onOpenChange={(open) => !open && setDeleteCompanyId(null)}>
        <DialogContent className="max-w-md bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-500" /> Delete Company
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this company?
              Any clients linked to this company will remain in the workspace but will be unlinked.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <Button variant="secondary" onClick={() => setDeleteCompanyId(null)}>Cancel</Button>
            <Button
              onClick={handleDeleteCompany}
              disabled={deletingCompany}
              variant="destructive"
            >
              {deletingCompany ? 'Deleting...' : 'Delete Company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Company Dialog */}
      <Dialog open={addCompanyOpen} onOpenChange={(open) => { setAddCompanyOpen(open); if (!open) setCompanyName(''); }}>
        <DialogContent className="max-w-2xl bg-card border-border p-0 overflow-hidden">
          <DialogHeader className="px-6 py-4 border-b border-border bg-muted/20">
            <DialogTitle className="text-xl font-bold text-foreground flex items-center gap-2">
              <Building2 className="w-5 h-5 text-workspace-accent" /> Add New Company
            </DialogTitle>
            <DialogDescription className="sr-only">Add a new company to your workspace</DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[80vh]">
            <div className="p-6 space-y-8">
              <div className="space-y-4">
                <Label className="text-base font-semibold text-foreground">Company Logo</Label>
                <div
                  onClick={() => companyFileInputRef.current?.click()}
                  className="relative group cursor-pointer"
                >
                  <div className={`
                    w-full aspect-[4/1] md:aspect-[5/1] rounded-xl border-2 border-dashed 
                    flex flex-col items-center justify-center gap-2 transition-all
                    ${avatarUrl ? 'border-workspace-accent bg-workspace-accent/5' : 'border-border hover:border-workspace-accent/50 hover:bg-muted/50'}
                  `}>
                    {uploadingAvatar ? (
                      <LoadingSpinner className="h-6 w-6" />
                    ) : avatarUrl ? (
                      <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 rounded-xl border-2 border-workspace-accent overflow-hidden shadow-lg bg-card flex items-center justify-center">
                          <img src={avatarUrl} alt="Preview" className="w-full h-full object-cover" />
                        </div>
                        <span className="text-xs font-medium text-workspace-accent">Click to change image</span>
                      </div>
                    ) : (
                      <>
                        <div className="p-3 rounded-full bg-workspace-accent/10 text-workspace-accent group-hover:scale-110 transition-transform">
                          <Upload className="w-6 h-6" />
                        </div>
                        <div className="text-center">
                          <p className="text-sm font-semibold text-workspace-accent">Click to upload <span className="text-muted-foreground font-normal">or Drag and drop</span></p>
                          <p className="text-xs text-muted-foreground mt-1">PNG, JPG, JPEG (max. 5MB)</p>
                        </div>
                      </>
                    )}
                  </div>
                  <input
                    type="file"
                    ref={companyFileInputRef}
                    onChange={handleAvatarUpload}
                    accept="image/*"
                    className="hidden"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-foreground flex items-center gap-1.5">Company Name <span className="text-red-500">*</span></Label>
                <div className="relative">
                  <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Enter Company Name"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="bg-background border-input text-foreground h-11 pl-10"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label className="text-foreground flex items-center gap-1.5">Website</Label>
                  <div className="relative">
                    <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="https://example.com"
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      className="bg-background border-input text-foreground h-11 pl-10"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-foreground flex items-center gap-1.5">Primary Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="email"
                      placeholder="Enter Email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="bg-background border-input text-foreground h-11 pl-10"
                    />
                  </div>
                </div>
              </div>


              {/* Client Selection */}
              <div className="space-y-4 pt-2 border-t border-border">
                <h3 className="text-base font-semibold text-foreground flex items-center gap-2">
                  <Users className="w-4 h-4 text-workspace-accent" /> Link Existing Clients
                </h3>
                <div className="space-y-2 max-h-[160px] overflow-y-auto pr-2 custom-scrollbar border border-border rounded-lg p-2 bg-background">
                  {clients.length === 0 ? (
                    <div className="p-4 text-center text-sm text-muted-foreground">No clients available to link</div>
                  ) : (
                    clients.map(client => (
                      <div
                        key={client.id}
                        className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors border ${selectedCompanyClientIds.includes(client.id)
                            ? 'bg-workspace-accent/10 border-workspace-accent/30 text-foreground'
                            : 'bg-transparent border-transparent text-muted-foreground hover:bg-muted/50'
                          }`}
                        onClick={() => {
                          setSelectedCompanyClientIds(prev =>
                            prev.includes(client.id) ? prev.filter(id => id !== client.id) : [...prev, client.id]
                          );
                        }}
                      >
                        <div className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${selectedCompanyClientIds.includes(client.id)
                            ? 'bg-workspace-accent border-workspace-accent text-white'
                            : 'border-input bg-background'
                          }`}>
                          {selectedCompanyClientIds.includes(client.id) && <Check className="w-3 h-3" />}
                        </div>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {client.avatar ? (
                            <img src={client.avatar} alt="" className="w-6 h-6 rounded-full object-cover flex-shrink-0" />
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                              <User className="w-3 h-3 text-muted-foreground" />
                            </div>
                          )}
                          <div className="truncate">
                            <p className="text-sm font-medium leading-none truncate">{client.name}</p>
                            {client.email && <p className="text-xs text-muted-foreground truncate">{client.email}</p>}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground pl-1 italic">
                  Select clients to add them to this company immediately.
                </p>
              </div>
            </div>
          </ScrollArea>
          <DialogFooter className="px-6 py-4 border-t border-border bg-muted/20 gap-3">
            <Button variant="outline" onClick={() => setAddCompanyOpen(false)} className="h-11 px-6">Cancel</Button>
            <Button
              onClick={handleAddCompany}
              disabled={addingCompany || !companyName.trim()}
              className="bg-workspace-accent text-white hover:bg-workspace-accent-hover h-11 px-8 shadow-md hover:shadow-lg transition-all"
            >
              {addingCompany ? 'Creating...' : 'Create Company'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contacts;
